/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { XCSSAllProperties } from '@compiled/react';

import { cssMap, cx, jsx, type StrictXCSSProp } from '@atlaskit/css';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import ChevronRight from '@atlaskit/icon/core/chevron-right';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	summary: {
		userSelect: 'none',
		display: 'flex',
		gap: token('space.050'),
		alignItems: 'center',
		justifyContent: 'left',
		width: 'auto',
		minHeight: '24px',
		cursor: 'pointer',
		marginTop: token('space.050'),
		marginBottom: token('space.050'),
		// default color, can be overridden with xcssColor prop
		color: token('color.text.subtlest'),
	},
	pressable: {
		backgroundColor: 'transparent',
	},
	animationContainer: {
		overflow: 'hidden',
	},
	children: {
		paddingBlockStart: token('space.050'),
	},
	expandIcons: {
		marginLeft: token('space.negative.050'),
		paddingLeft: token('space.050'),
	},
});

export interface ExpanderProps {
	children: React.ReactNode;
	isExpanded: boolean;
	showText: JSX.Element | string;
	hideText?: JSX.Element | string;
	onClick?: () => void;
	xcss?: StrictXCSSProp<'color', never>;
}

export const UncontrolledExpander = ({
	children,
	showText,
	hideText,
	onClick,
	isExpanded,
	xcss,
}: ExpanderProps): JSX.Element | null => {
	// Prevent expand from displaying if there is no content
	if (!children) {
		return null;
	}

	return (
		<Box>
			<Pressable
				xcss={cx(
					styles.summary,
					styles.pressable,
					// the type defined above is correct, typescript yells when passed to xcss
					xcss as StrictXCSSProp<XCSSAllProperties, never>,
				)}
				onClick={onClick}
			>
				<Box>{isExpanded ? (hideText ?? showText) : showText}</Box>
				<Box xcss={styles.expandIcons}>
					{isExpanded ? (
						<ChevronDown label="" size="small" />
					) : (
						<ChevronRight label="" size="small" />
					)}
				</Box>
			</Pressable>
			<Box xcss={styles.animationContainer}>
				<ExitingPersistence>
					{isExpanded && (
						<FadeIn entranceDirection="top" exitDirection="top" duration="medium">
							{(motionProps) => {
								return (
									<div {...motionProps}>
										<Box xcss={styles.children}>{children}</Box>
									</div>
								);
							}}
						</FadeIn>
					)}
				</ExitingPersistence>
			</Box>
		</Box>
	);
};
